import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ExternalLink, Send } from 'lucide-react';

// Obfuscate the contact email: split into parts and assemble only at send-time,
// never written as a plain string in the DOM or source.
function buildContactHref(name: string, message: string): string {
  const raw = import.meta.env.VITE_CONTACT_EMAIL as string | undefined;
  if (!raw) return '#';
  // Split at '@' so the full address never appears as a single string literal
  const [localPart, domain] = raw.split('@');
  const address = [localPart, domain].join('\u0040');
  const subject = encodeURIComponent('Contact from Meshpit');
  const body = encodeURIComponent(`Name: ${name}\n\n${message}`);
  return `mailto:${address}?subject=${subject}&body=${body}`;
}

const NAV_LINKS = [
  { label: 'Explore', to: '/explore' },
  { label: 'Changelog', to: '/changelog' },
  { label: 'Blog', to: '/blog' },
  { label: 'API Reference', to: '/blog/meshpit-api-reference' },
  { label: 'Cheatsheet', to: '/cheatsheet' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Terms', to: '/terms' },
];

const TECH_LINKS = [
  { label: 'OpenSCAD', href: 'https://openscad.org' },
  { label: 'Three.js', href: 'https://threejs.org' },
  { label: 'shadcn/ui', href: 'https://ui.shadcn.com' },
  { label: 'Monaco', href: 'https://microsoft.github.io/monaco-editor/' },
  { label: 'Vite', href: 'https://vitejs.dev' },
  { label: 'Stripe', href: 'https://stripe.com' },
  { label: 'Supabase', href: 'https://supabase.com' },
];

export function Footer() {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const linkRef = useRef<HTMLAnchorElement>(null);

  const handleSend = () => {
    if (!name.trim() || !message.trim()) return;
    const href = buildContactHref(name.trim(), message.trim());
    // Programmatically trigger via a hidden anchor so the address is never
    // rendered in the DOM as readable text
    if (linkRef.current) {
      linkRef.current.href = href;
      linkRef.current.click();
    }
    setName('');
    setMessage('');
  };

  return (
    <footer className="border-t border-border bg-background">
      {/* Hidden anchor used only at send-time — never carries a visible href */}
      <a
        ref={linkRef}
        href="#"
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
      />

      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
          {/* Column 1 — Contact & Copyright */}
          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-semibold text-foreground">Contact</h3>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label
                  htmlFor="footer-name"
                  className="text-xs text-muted-foreground"
                >
                  Your name
                </Label>
                <Input
                  id="footer-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="h-8 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label
                  htmlFor="footer-message"
                  className="text-xs text-muted-foreground"
                >
                  Message
                </Label>
                <Textarea
                  id="footer-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Say hello…"
                  rows={3}
                  className="resize-none text-sm"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 self-start"
                onClick={handleSend}
                disabled={!name.trim() || !message.trim()}
              >
                <Send className="h-3.5 w-3.5" />
                Send
              </Button>
            </div>
            <p className="mt-auto pt-4 text-xs text-muted-foreground">
              &copy; 2026 by Fredi Bach
            </p>
          </div>

          {/* Column 2 — Navigation */}
          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-semibold text-foreground">
              Navigation
            </h3>
            <ul className="flex flex-col gap-2">
              {NAV_LINKS.map(({ label, to }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — Built on top of giants */}
          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-semibold text-foreground">
              Built on top of giants
            </h3>
            <ul className="flex flex-col gap-2">
              {TECH_LINKS.map(({ label, href }) => (
                <li key={href}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {label}
                    <ExternalLink className="h-3 w-3 opacity-50" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
